import { FC } from 'react';

import { ComponentWrapper, SelectSelectionChangeEvent, TextInput, TextInputChangeEvent } from '@/components';
import { DefaultSelect } from '@/components/forms/DefaultSelect';
import { MemoPhoneInput } from '@/components/forms/PhoneInput';
import { ErrorResponse, Options } from '@/core';
import { Country } from '@/store/webApi';

interface ContactDetailsProps {
  addBackground?: boolean;
  countryLabel: string;
  countries: Options<Country>;
  countriesError?: ErrorResponse;
  countriesIsLoading: boolean;
  defaultCountryId?: number;
  defaultPostalOne?: string;
  defaultPostalTwo?: string;
  defaultTelephoneNumber?: string;
  defaultTownCity?: string;
  defaultZip?: string;
  hidePhoneNumber?: boolean;
  onCountryChange: SelectSelectionChangeEvent<Country>;
  onPostalOneChange: TextInputChangeEvent;
  onPostalTwoChange: TextInputChangeEvent;
  onTelephoneNumberChange: (s?: string) => void;
  onTownCityChange: TextInputChangeEvent;
  onZipChange: TextInputChangeEvent;
  postalOneLabel: string;
  postalTwoAriaLabel: string;
  requiredText: string;
  telephoneNumberLabel: string;
  townCityLabel: string;
  whiteBackground?: boolean;
  zipLabel: string;
}

export const ContactDetails: FC<ContactDetailsProps> = ({
  addBackground = true,
  countryLabel,
  countries,
  countriesError,
  countriesIsLoading,
  defaultCountryId,
  defaultTelephoneNumber,
  defaultPostalOne,
  defaultPostalTwo,
  defaultTownCity,
  defaultZip,
  hidePhoneNumber = false,
  onCountryChange,
  onPostalOneChange,
  onPostalTwoChange,
  onTelephoneNumberChange,
  onTownCityChange,
  onZipChange,
  postalOneLabel,
  postalTwoAriaLabel,
  requiredText,
  telephoneNumberLabel,
  townCityLabel,
  whiteBackground,
  zipLabel,
}): JSX.Element => {
  return (
    <ComponentWrapper addBackground={addBackground} whiteBackground={whiteBackground} id="contact-details">
      {!hidePhoneNumber && (
        <MemoPhoneInput
          id="telephone-number"
          label={telephoneNumberLabel}
          defaultValue={defaultTelephoneNumber}
          onChangeNumber={onTelephoneNumberChange}
          required
        />
      )}
      <DefaultSelect 
        id="country-of-residence"
        name={countryLabel}
        label={countryLabel}
        defaultValue={defaultCountryId?.toString()}
        onChange={onCountryChange}
        options={countries.map(item => { return { value: item.value, label: item.text}})}
      />
      <TextInput
        defaultText={defaultPostalOne}
        id="postal-address-one"
        inputType="text"
        label={postalOneLabel}
        onValueChange={onPostalOneChange}
        required
        requiredText={requiredText}
      ></TextInput>
      <TextInput
        aria-label={postalTwoAriaLabel}
        defaultText={defaultPostalTwo}
        formPreventControlRegistration
        id="postal-address-two"
        inputType="text"
        onValueChange={onPostalTwoChange}
      ></TextInput>
      <TextInput
        defaultText={defaultTownCity}
        id="town-city"
        inputType="text"
        label={townCityLabel}
        onValueChange={onTownCityChange}
        required
        requiredText={requiredText}
      ></TextInput>
      <TextInput
        defaultText={defaultZip}
        id="zip-code"
        inputType="text"
        label={zipLabel}
        onValueChange={onZipChange}
        required
        requiredText={requiredText}
        debounceWait={0}
      ></TextInput>
    </ComponentWrapper>
  );
};
